import { WrapDivChange } from "@/store/zustand/changeLedgerTypes";
import { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import {
  NavTreeUpdates,
  fixUniquePathsForAllChildren,
} from "./navTreeLookupUtils";
import { getElementsForCodebaseId } from "./domUtils";
import { ApplyInstantUpdateProps } from "./utils";

export const applyWrapInDiv = (props: ApplyInstantUpdateProps) => {
  const { iframeDocument, storyboardId, persistentData } = props;
  const change = props.change as WrapDivChange;

  const changeFields = change.changeFields;
  const codebaseIdsToWrap = changeFields.codebaseIdsToWrap;

  const firstCodebaseId = codebaseIdsToWrap[0];

  // We assume the other codebase IDs are siblings of this codebase ID, so we
  // find each instance of the first one and include the other items that match in it
  // If the other items aren't all found, we do not wrap and let hot reload handle it
  const itemsToWrap = getElementsForCodebaseId(iframeDocument, firstCodebaseId);

  if (!itemsToWrap.length) {
    return;
  }

  persistentData.storyboardsAppliedTo.add(storyboardId);

  for (const item of itemsToWrap) {
    const otherCodebaseIds = codebaseIdsToWrap.slice(1);

    // For each codebase ID in otherCodebaseIds, retrieve the element that is a sibling of item
    const parentElement = item.parentElement;
    const allChildren = Array.from(parentElement?.children || []);
    const allItemsToAddToNewDiv: Element[] = [item];

    let earliestIndex = allChildren.indexOf(item);

    otherCodebaseIds.forEach((codebaseId) => {
      const foundSibling = allChildren.find(
        (child) => child.getAttribute("data-tempoelementid") === codebaseId,
      );
      if (foundSibling) {
        allItemsToAddToNewDiv.push(foundSibling);
        const index = allChildren.indexOf(foundSibling);
        if (index < earliestIndex) {
          earliestIndex = index;
        }
      }
    });

    // TODO: What to do if they all can't be found?
    if (allItemsToAddToNewDiv.length !== codebaseIdsToWrap.length) {
      // For now, just add the ones that were found
    }

    // Create a div with a clone of the item, while hiding the item
    // When the hot reload happens the clone gets deleted and the item is shown again
    const newDiv = iframeDocument.createElement("div");
    newDiv.className = change.changeFields.newCodebaseId;
    newDiv.setAttribute(
      "data-tempoelementid",
      change.changeFields.newCodebaseId,
    );
    allItemsToAddToNewDiv.forEach((elem) => {
      newDiv.appendChild(elem);
    });

    // Insert the new div where the earliest item was
    // Get the item before the earliest item, then find the next real sibling that is not in allItemsToAddToNewDiv
    let toInsertBefore: Element | null;

    if (earliestIndex > 0) {
      toInsertBefore = allChildren[earliestIndex - 1]
        .nextSibling as Element | null;
    } else {
      toInsertBefore = parentElement?.firstChild as Element | null;
    }

    parentElement?.insertBefore(newDiv, toInsertBefore);
  }
};

export const undoWrapInDiv = (props: ApplyInstantUpdateProps) => {
  const { iframeDocument } = props;
  const change = props.change as WrapDivChange;

  const { codebaseIdsToWrap } = change.changeFields;

  // Find all wrapper divs that were created
  const wrapperDivs = getElementsForCodebaseId(
    iframeDocument,
    change.changeFields.newCodebaseId,
  );

  for (const wrapperDiv of wrapperDivs) {
    const parentElement = wrapperDiv.parentElement;
    if (!parentElement) continue;

    // Check if this wrapper contains the elements we're looking for
    const wrappedElements = Array.from(wrapperDiv.children);
    const hasExpectedElements = codebaseIdsToWrap.some((codebaseId) =>
      wrappedElements.some(
        (child) => child.getAttribute("data-tempoelementid") === codebaseId,
      ),
    );

    if (hasExpectedElements) {
      // Move all children of the wrapper back to the wrapper's parent
      // Insert them where the wrapper currently is
      const nextSibling = wrapperDiv.nextSibling;

      // Move children back to parent
      wrappedElements.forEach((child) => {
        parentElement.insertBefore(child, nextSibling);
      });

      // Remove the empty wrapper div
      parentElement.removeChild(wrapperDiv);
    }
  }
};

export const wrapNodesInDiv = (
  nodesToWrap: NavTreeNode[],
  storyboardId: string,
  wrapperDivClasses: string[],
  wrappedCodebaseId: string,
  parentNode?: NavTreeNode,
) => {
  // Create wrapper div node
  const wrapperNode: NavTreeNode = {
    parent: parentNode,
    children: nodesToWrap,
    classList: wrapperDivClasses,
    name: "div",
    elementTagName: "div",
    isComponent: false,
    tempoElement: new TempoElement(
      wrappedCodebaseId,
      storyboardId,
      nodesToWrap[0].tempoElement.uniquePath,
    ),
    level: nodesToWrap[0].level,
    domUniquePath: nodesToWrap[0].domUniquePath,
  };

  nodesToWrap.forEach((node) => {
    node.parent = wrapperNode;
  });

  return wrapperNode;
};

// Update nav tree for WRAP_DIV change - modifies in place and returns updated nav tree
export const applyWrapDivChangeToNavTree = (
  props: ApplyInstantUpdateProps,
): void => {
  const { navTreeUpdates, storyboardId } = props;
  const change = props.change as WrapDivChange;

  const codebaseIdsToWrap = change.changeFields.codebaseIdsToWrap;
  if (codebaseIdsToWrap.length === 0) return;

  // Find nodes to wrap using the lookup for efficiency
  const nodesToWrap: NavTreeNode[] = [];
  let parentNode: NavTreeNode | null = null;

  const originalNodeIndexesInParent: {
    [codebaseId: string]: number;
  } = {};

  for (const codebaseId of codebaseIdsToWrap) {
    const nodes = navTreeUpdates.codebaseIdLookup[codebaseId] || [];
    if (nodes.length > 0) {
      const node = nodes[0]; // Take first occurrence
      nodesToWrap.push(node);

      originalNodeIndexesInParent[codebaseId] =
        node.parent?.children?.indexOf(node) || 0;

      // Find parent from first node
      if (!parentNode) {
        parentNode = node.parent || null;
      }
    }
  }

  // Save for instant update undo
  change.changeFields.originalNodeIndexesInParent = originalNodeIndexesInParent;

  if (nodesToWrap.length === 0 || !parentNode) return;

  // Create wrapper div node
  const wrapperNode: NavTreeNode = {
    parent: parentNode,
    children: nodesToWrap,
    classList: [],
    name: "div",
    elementTagName: "div",
    isComponent: false,
    tempoElement: new TempoElement(
      change.changeFields.newCodebaseId,
      storyboardId,
      nodesToWrap[0].tempoElement.uniquePath,
    ),
    level: nodesToWrap[0].level,
    domUniquePath: nodesToWrap[0].domUniquePath,
  };

  nodesToWrap.forEach((node) => {
    node.parent = wrapperNode;
  });

  // Replace wrapped nodes with wrapper in parent's children
  const firstIndex = Math.min(
    ...nodesToWrap.map((n) => parentNode!.children!.indexOf(n)),
  );
  parentNode.children =
    parentNode.children?.filter((child) => !nodesToWrap.includes(child)) || [];
  parentNode.children.splice(firstIndex, 0, wrapperNode);

  // Fix the unique paths of all children
  fixUniquePathsForAllChildren(parentNode);
};

// Undo nav tree changes for WRAP_DIV change - removes wrapper and restores original structure
export const undoWrapDivChangeToNavTree = (
  props: ApplyInstantUpdateProps,
): void => {
  const { navTreeUpdates } = props;
  const change = props.change as WrapDivChange;

  const codebaseIdsToWrap = change.changeFields.codebaseIdsToWrap;
  if (codebaseIdsToWrap.length === 0) return;

  // Find the wrapper node by looking for the placeholder codebase ID
  const findWrapperNode = (node: NavTreeNode): NavTreeNode | null => {
    if (node.tempoElement?.codebaseId === change.changeFields.newCodebaseId) {
      return node;
    }
    for (const child of node.children || []) {
      const found = findWrapperNode(child);
      if (found) return found;
    }
    return null;
  };

  const wrapperNode = findWrapperNode(navTreeUpdates.navTree);
  if (!wrapperNode || !wrapperNode.parent) return;

  const parentNode = wrapperNode.parent;
  const wrappedNodes = wrapperNode.children || [];

  // Find where the wrapper is in parent's children and replace it with wrapped nodes
  const wrapperIndex = parentNode.children?.indexOf(wrapperNode) || 0;

  // Remove wrapper from parent's children
  parentNode.children =
    parentNode.children?.filter((child) => child !== wrapperNode) || [];

  // Insert wrapped nodes back into parent at the original position
  const originalNodeIndexesInParent =
    change.changeFields.originalNodeIndexesInParent || {};
  wrappedNodes.forEach((node) => {
    node.parent = parentNode;
    let indexToPlace =
      originalNodeIndexesInParent[node.tempoElement.codebaseId];
    if (indexToPlace === undefined) {
      indexToPlace = wrapperIndex;
    }

    parentNode.children?.splice(indexToPlace, 0, node);
  });

  // Fix the unique paths of all children
  fixUniquePathsForAllChildren(parentNode);
};

// Consolidated apply function that handles both DOM and nav tree
export const applyWrapDivChange = (props: ApplyInstantUpdateProps): void => {
  // Apply DOM changes
  applyWrapInDiv(props);

  // Apply nav tree changes
  if (!props.skipUpdateNavTree) {
    applyWrapDivChangeToNavTree(props);
  }
};

// Consolidated undo function that handles both DOM and nav tree
export const undoWrapDivChange = (props: ApplyInstantUpdateProps): void => {
  // Undo DOM changes
  undoWrapInDiv(props);

  // Undo nav tree changes
  if (!props.skipUpdateNavTree) {
    undoWrapDivChangeToNavTree(props);
  }
};
